import { Link } from 'react-router-dom';
import { FEATURES } from '../data/features';
import { FEATURE_GROUPS } from '../data/navigation';
import { CTABanner } from '../components/marketing/CTABanner';
import { SEO } from '../components/SEO';
import { Breadcrumb } from '../components/marketing/Breadcrumb';

export function FeaturesOverviewPage(): JSX.Element {
  return (
    <>
      <SEO
        title="Features"
        description="Everything Resida does for independent landlords: Zelle rent tracking, Craigslist and Facebook listings with an AI leasing assistant, AI-coordinated repairs, and Claude and ChatGPT integration, plus leases, walkthroughs, expenses, and tenant messaging."
        path="/features"
      />

      {/* Hero */}
      <section className="pt-12 pb-16 bg-[var(--landing-bg)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb
            items={[
              { label: 'Home', href: '/' },
              { label: 'Features' },
            ]}
          />
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--landing-text-primary)] leading-tight mb-4">
              Everything you need to manage your rentals
            </h1>
            <p className="text-lg text-[var(--landing-text-muted)]">
              Built for independent landlords with 1 to 20 units. No bloat, no
              features you&apos;ll never use.
            </p>
          </div>
        </div>
      </section>

      {/* Feature groups */}
      {FEATURE_GROUPS.map((group) => (
        <section
          key={group.group}
          className="py-[var(--landing-section-py)] odd:bg-[var(--landing-bg)] even:bg-[var(--landing-surface-alt)]"
        >
          <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-sm font-medium uppercase tracking-widest text-[var(--landing-accent-text)] mb-3">
              {group.headline}
            </h2>
            <p className="text-[var(--landing-text-muted)] max-w-3xl mb-8">{group.summary}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {group.items.map((navItem) => {
                const feature = FEATURES.find((f) => `/features/${f.slug}` === navItem.href);
                return (
                  <Link
                    key={navItem.href}
                    to={navItem.href}
                    className="bg-[var(--landing-surface)] rounded-[var(--landing-radius)] p-6 border border-[var(--landing-border)] hover:border-[var(--landing-accent)] transition-colors group block"
                  >
                    <h3 className="text-lg font-semibold text-[var(--landing-text-primary)] group-hover:text-[var(--landing-accent-text)] transition-colors mb-2">
                      {navItem.label}
                    </h3>
                    <p className="text-sm text-[var(--landing-text-muted)] leading-relaxed mb-3">
                      {feature?.subtitle ?? navItem.description}
                    </p>
                    <span className="text-sm text-[var(--landing-accent-text)] font-medium">
                      Learn more &rarr;
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      <CTABanner />
    </>
  );
}
